import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether this person has asked their device for less movement.
 *
 * ── WHY THIS IS NOT A PREFERENCE WE OFFER ───────────────────────────────────
 *
 * Because they have already expressed it, at the operating system, once, for
 * every app. Asking again in a settings screen inside ChiCare is asking
 * somebody to tell us a thing they have told their phone.
 *
 * ── WHY IT MATTERS MORE THAN IT SOUNDS ──────────────────────────────────────
 *
 * For somebody with a vestibular disorder, a shop front that slides and zooms
 * by itself is not merely unpleasant — it causes nausea and dizziness, and the
 * page becomes one they close. So the honest response is to remove the motion
 * and keep every function, not to slow the animation down.
 *
 * Subscribed rather than read once: this changes while the app is open, because
 * on both iOS and Android it is a toggle in accessibility settings that people
 * reach for in the middle of feeling unwell.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    // Guarded for the server and for the Jest-style environments that have no
    // matchMedia. Defaulting to false there is right: the animation is the
    // normal case, and a test environment is nobody's inner ear.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const list = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);

    // No re-read here on purpose. The lazy initializer above already read
    // matchMedia during the first render, and this listener covers every
    // change after it. Re-reading would schedule a second render on every
    // mount to cover a window of one render pass — and the thing being watched
    // is a toggle in system settings, not something that flips in microseconds.
    list.addEventListener('change', onChange);

    return () => list.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
